/**
 * Play `urls` in order on a single audio element — each clip auto-advances
 * to the next when it ends. Falsy URLs are skipped. This mirrors the
 * device, which plays a cached greeting clip then the body clip, so the
 * in-app preview is byte-faithful to what the elder hears.
 *
 * Fire-and-forget by design (returns void, kicks off playback synchronously):
 * the caller's "generating…" spinner should track the *fetch*, not the
 * playback, and nothing should ever `await` a clip ending — a media element
 * that stalls without firing `ended` must not be able to strand UI state.
 *
 * Uses the `onended`/`onerror` *properties* (not addEventListener) so a
 * second call cleanly supersedes an in-flight sequence: the new call
 * overwrites the handlers, and the old sequence simply stops advancing.
 *
 * If `play()` is rejected (browser blocking programmatic playback) we leave
 * the clip cued with its `onended` handler set — the `<audio controls>` lets
 * the user press play, and the next clip still follows when it ends.
 */
export function playClipsSequentially(
  audio: HTMLAudioElement,
  urls: Array<string | null | undefined>,
): void {
  const queue = urls.filter((u): u is string => Boolean(u));
  let i = 0;

  const advance = (): void => {
    const url = queue[i];
    i += 1;
    if (url === undefined) {
      audio.onended = null;
      audio.onerror = null;
      return;
    }
    audio.src = url;
    audio.onended = advance;
    audio.onerror = advance;
    void audio.play().catch(() => {
      // Programmatic playback blocked — leave the clip cued (onended stays
      // set, so a manual press still chains to the next clip). Don't
      // auto-advance, or we'd silently skip past the blocked clip.
    });
  };

  advance();
}
